import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useOrderApi } from "api/OrderApi/order-api";
import _ from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { OrderDeliveryStatus } from "utils/enums";
import { OrderParametersDialogProps } from "./interfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { getOrderDeliveryStatusEnumTranslation } from "utils/enums-helper";
import { setOrderParameters } from "./order-events";
import { css } from "@emotion/css";
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import TimePicker from 'react-time-picker';
import moment from "moment";
import { getShiftDateFromDate, getShiftStartDateFromDate } from "utils/helpers";

export const OrderParametersDialog: React.FC<OrderParametersDialogProps> = (
  props: OrderParametersDialogProps
) => {
  const { t } = useTranslation(["commonTranslation", "enumTranslation"]);
  const { setParameters } = useOrderApi();
  const [open, setOpen] = useState<boolean>(false);
  const [selectedNewDeliveryStatus, setSelectedNewDeliveryStatus] =
    useState<OrderDeliveryStatus | undefined>(undefined);
  const [isOnTimeDelivery, setIsOnTimeDelivery] = useState<boolean>(
    props.isOnTimeDelivery
  );
  const [readyDate, setReadyDate] = useState<string>(props.readyDate);
  const [readyTime, setReadyTime] = useState<string>(moment(props.readyDate).format("HH:mm"));
  const [selectedReadyDateMinutes, setSelectedReadyDateMinutes] = useState<number>(0);
  const readyDateMinutes = [15, 30, 45, 60, 75, 90]

  const handleClickOpen = () => {
    setSelectedNewDeliveryStatus(undefined);
    setSelectedReadyDateMinutes(0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async (): Promise<void> => {
      const shiftStartDate = getShiftStartDateFromDate(moment());
      const shiftDate = getShiftDateFromDate(moment());
      const [hours, minutes] = readyTime.split(":").map(Number);
      let readyDateWithTime = shiftDate.clone().hour(hours).minute(minutes);
      if(readyDateWithTime.isBefore(shiftStartDate)){
        readyDateWithTime = readyDateWithTime.add(1, 'days');
      }
      await setParameters({
        orderId: props.orderId,
        deliveryStatus: selectedNewDeliveryStatus,
        isOnTimeDelivery: isOnTimeDelivery,
        readyDate: props.isOrderReadyDateChangeEnabled
          ? readyDateWithTime.format("YYYY-MM-DDTHH:mm:ss")
          : undefined,
      }).then(() => {
        setOrderParameters({
          orderId: props.orderId,
          deliveryStatus: selectedNewDeliveryStatus,
          isOnTimeDelivery: isOnTimeDelivery,
          readyDate: props.isOrderReadyDateChangeEnabled
            ? readyDateWithTime.format("YYYY-MM-DDTHH:mm:ss")
            : undefined,
        });
        setOpen(false);
      });
  };

  return (
    <>
      <IconButton color="primary" component="label" onClick={handleClickOpen}>
        <FontAwesomeIcon icon={faEdit} size="sm" title="" />
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="md"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle>{t("orderParameters")}</DialogTitle>
        <DialogContent>
          <Box>
            <Grid container spacing={0.5}>
              <Grid item xs={3}>
                <Typography variant="subtitle1">{t("orderStatus")}:</Typography>
                <Typography variant="h5">
                  {_.toUpper(
                    getOrderDeliveryStatusEnumTranslation(
                      props.deliveryStatus,
                      t
                    )
                  )}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                {props.deliveryStatus ===
                  OrderDeliveryStatus.ReadyForCollect && (
                  <Button
                    size="large"
                    variant={
                      selectedNewDeliveryStatus === undefined
                        ? "outlined"
                        : "contained"
                    }
                    onClick={() => {
                      if (selectedNewDeliveryStatus === undefined) {
                        setSelectedNewDeliveryStatus(OrderDeliveryStatus.New);
                      } else {
                        setSelectedNewDeliveryStatus(undefined);
                      }
                    }}
                  >
                    {_.toUpper(
                      getOrderDeliveryStatusEnumTranslation(
                        OrderDeliveryStatus.New,
                        t
                      )
                    )}
                  </Button>
                )}
                {props.deliveryStatus === OrderDeliveryStatus.New && (
                  <Button
                    size="large"
                    variant={
                      selectedNewDeliveryStatus === undefined
                        ? "outlined"
                        : "contained"
                    }
                    onClick={() => {
                      if (selectedNewDeliveryStatus === undefined) {
                        setSelectedNewDeliveryStatus(
                          OrderDeliveryStatus.ReadyForCollect
                        );
                      } else {
                        setSelectedNewDeliveryStatus(undefined);
                      }
                    }}
                  >
                    {_.toUpper(
                      getOrderDeliveryStatusEnumTranslation(
                        OrderDeliveryStatus.ReadyForCollect,
                        t
                      )
                    )}
                  </Button>
                )}
              </Grid>

              {(props.deliveryStatus === OrderDeliveryStatus.ReadyForCollect ||
                props.deliveryStatus === OrderDeliveryStatus.New) && (
                <>
                  {props.isOrderReadyDateChangeEnabled && (
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2">
                            {t("maxDeliveryTime")}:
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          className={css`
                            padding-top: 0 !important;
                          `}
                        >
                          <TimePicker
                            onChange={(date: any) => setReadyTime(date)}
                            value={readyTime}
                            disableClock={true}
                            format="HH:mm"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={10}
                          className={css`
                            padding-top: 0 !important;
                          `}
                        >
                          {_.map(readyDateMinutes, (d, index) => (
                            <Button
                              variant={
                                d === selectedReadyDateMinutes
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() => {
                                if (selectedReadyDateMinutes !== d) {
                                  setSelectedReadyDateMinutes(d);
                                  setReadyTime(
                                    moment()
                                      .add(d, "minutes")
                                      .format("HH:mm")
                                  );
                                } else {
                                  setSelectedReadyDateMinutes(0);
                                  setReadyTime(
                                    moment(readyDate).format("HH:mm")
                                  );
                                }
                              }}
                              className={css`
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                margin-left: 10px;
                              `}
                            >{`${d} min`}</Button>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isOnTimeDelivery}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                            isChecked: boolean
                          ) => {
                            setIsOnTimeDelivery(isChecked);
                          }}
                        />
                      }
                      label={t("isOnTimeDelivery")}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave}>
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
